import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { setIpRestrictionBypass } from '../../../../api/firebase/function/security/securityIpFunction';
import { IP_RESTRICTION_BYPASS_TOGGLE_ERROR } from '../../../../constants/labels';
import {
  gray40,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import { dialogText, dialogTitle } from '../../../../constants/location';

interface Props {
  open: boolean;
  companyId: string;
  /** 変更前の一時無効化状態。ダイアログはこの反対の状態へ切り替える前提で開く。 */
  currentDisabled: boolean;
  onClose: () => void;
  /** 切替成功時に新しい一時無効化状態を返す。 */
  onChanged: (afterDisabled: boolean) => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bodyText: {
    ...dialogText,
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

/**
 * 運営 admin 用: 企業の IP 制限 一時無効化（ip_restriction_disabled）切替ダイアログ（#2982）。
 * 全許可 IP の誤設定でテナントがロックアウトした際の緊急救済経路。
 * セキュリティアドオントグルと同じく企業詳細「概要」タブの行から開く。
 */
const IpRestrictionBypassToggleDialog: React.FC<Props> = ({
  open,
  companyId,
  currentDisabled,
  onClose,
  onChanged,
}) => {
  const pendingDisabled = !currentDisabled;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      await setIpRestrictionBypass({
        company_id: companyId,
        disabled: pendingDisabled,
      });
      onChanged(pendingDisabled);
      onClose();
    } catch (e) {
      Sentry.captureException(e);
      setError(e instanceof Error ? e.message : IP_RESTRICTION_BYPASS_TOGGLE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          IP制限を{pendingDisabled ? '一時無効化' : '再有効化'}しますか？
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={styles.fieldWrapper}>
        {error && <Box sx={styles.errorBox}>{error}</Box>}

        <Typography sx={styles.bodyText}>
          {pendingDisabled
            ? 'この企業の画面IP制限を一時的に無効化します。許可リスト誤設定によるロックアウトの救済用です。設定変更は最大5分で全体に反映されます。'
            : '一時無効化を解除し、この企業の画面IP制限（許可リストによる遮断）を再開します。設定変更は最大5分で全体に反映されます。'}
        </Typography>

        <Box sx={styles.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.actionButton]}
          >
            {pendingDisabled ? '一時無効化する' : '再有効化する'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default IpRestrictionBypassToggleDialog;
